/**
 * The money of a case: what it comes to, what has been paid of it, and what
 * may be done about either. The request of 8 October 2026 (fourth).
 *
 * A case's bill is its lines, as the receipt adds them, with its discounts and
 * charges. It may be paid in parts, each payment saying how it came, its
 * reference and its day. A doctor has a credit with the lab, added by
 * Accounts, which pays a case in whole or in part.
 *
 * The API holds the same rules under the same names, `CaseBill` and
 * `DoctorCredit`, with the same sentences, and is the one that decides. This
 * file is what the screens read, what a form checks before it sends, and what
 * the mock server answers with.
 */

/** How a payment reached the lab. `credit` is the doctor's own credit used on a case: only the system writes it. */
export const PAYMENT_METHODS = ['cash', 'transfer', 'cheque', 'card', 'credit'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** The four a person chooses from when money is received. */
export const RECEIVED_METHODS = ['cash', 'transfer', 'cheque', 'card'] as const;
export type ReceivedMethod = (typeof RECEIVED_METHODS)[number];

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  transfer: 'Bank transfer',
  cheque: 'Cheque',
  card: 'Card',
  credit: 'Doctor credit',
};

/** What a payment whose method nobody recorded reads as. */
export const METHOD_NOT_RECORDED = 'Not recorded';

export function isReceivedMethod(method: string | null | undefined): method is ReceivedMethod {
  return (RECEIVED_METHODS as readonly string[]).includes(method ?? '');
}

export function paymentMethodLabel(method: string | null | undefined): string {
  return (PAYMENT_METHODS as readonly string[]).includes(method ?? '')
    ? PAYMENT_METHOD_LABEL[method as PaymentMethod]
    : METHOD_NOT_RECORDED;
}

/** Where a case stands with its money. Part is some of it received and not all. */
export const PAYMENT_STATUSES = ['unpaid', 'part', 'paid'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  unpaid: 'Unpaid',
  part: 'Part paid',
  paid: 'Paid',
};

export const ADJUSTMENT_KINDS = ['discount', 'charge'] as const;
export type AdjustmentKind = (typeof ADJUSTMENT_KINDS)[number];

export const ADJUSTMENT_KIND_LABEL: Record<AdjustmentKind, string> = {
  discount: 'Discount',
  charge: 'Added charge',
};

export const ADJUSTMENT_MODES = ['amount', 'percent'] as const;
export type AdjustmentMode = (typeof ADJUSTMENT_MODES)[number];

/** A discount or a charge, as far as the sum needs it. */
export interface BillAdjustment {
  kind: AdjustmentKind;
  mode: AdjustmentMode;
  /** Money, or a percentage from 0 to 100. Never negative: the kind says which way it goes. */
  value: number;
}

/** What a case comes to and what has been paid of it. */
export interface BillTotals {
  /** The lines, as the receipt adds them. */
  subtotal: number;
  /** Discounts and charges together, signed. */
  adjustments: number;
  /** What the case comes to after them. Never below zero. */
  due: number;
  /** Everything received, credit used among it. */
  paid: number;
  /** What is still owed. Never below zero. */
  balance: number;
  status: PaymentStatus;
}

export const BILL_REASON_MAX = 200;
export const PAYMENT_REFERENCE_MAX = 128;
export const PAYMENT_NOTE_MAX = 300;
export const CREDIT_NOTE_MAX = 300;

/** The id of the one payment of a case paid before payments were kept one by one. */
export const RECORDED_PAYMENT_ID = 'recorded';

/** The API's refusals, word for word. */
export const BILL_REFUSAL = {
  badKind: 'Choose a discount or an added charge.',
  badMode: 'Choose an amount or a percentage.',
  badValue: 'Enter a figure above zero.',
  badPercent: 'A percentage is above 0 and no more than 100.',
  reasonRequired: 'Say why, so whoever reads the bill knows.',
  reasonTooLong: 'Keep the reason to 200 characters.',
  paid: 'This case is paid. Remove a payment before changing its bill.',
  belowPaid: 'That takes the bill below what has already been paid on this case.',
  noAdjustment: 'That adjustment is not on this case.',
  badAmount: 'Enter an amount above zero.',
  badMethod: 'Choose how it was paid: cash, bank transfer, cheque or card.',
  nothingOwed: 'This case owes nothing.',
  referenceTooLong: 'Keep the reference to 128 characters.',
  noteTooLong: 'Keep the note to 300 characters.',
  inTheFuture: 'A payment cannot be dated after today.',
  noPayment: 'That payment is not on this case.',
  creditStays: "A payment from the doctor's credit is removed, not changed.",
  noDoctor: "This case is not linked to a doctor's record, so it has no credit to use.",
} as const;

export const CREDIT_REFUSAL = {
  badAmount: 'Enter an amount above zero.',
  noteRequired: "Say what it is for, so the doctor's page explains itself.",
  noteTooLong: 'Keep the note to 300 characters.',
  badMethod: BILL_REFUSAL.badMethod,
} as const;

const cents = (value: number) => Math.round(value * 100) / 100;

/** A figure as the API's sentences say one: two places at the most, and no more than are needed. */
export function sentenceMoney(amount: number): string {
  return String(cents(amount));
}

export function moreThanOwed(balance: number): string {
  return `That is more than the ${sentenceMoney(balance)} this case still owes.`;
}

export function moreThanCredit(credit: number): string {
  return `The doctor's credit is ${sentenceMoney(credit)}.`;
}

export function moreThanBalance(balance: number): string {
  return `The doctor's credit is ${sentenceMoney(balance)}. No more than that can be taken off.`;
}

/** What one adjustment comes to, signed: below zero for a discount. A percentage is of the lines. */
export function adjustmentAmount(adjustment: BillAdjustment, subtotal: number): number {
  const size =
    adjustment.mode === 'percent' ? cents((subtotal * adjustment.value) / 100) : cents(adjustment.value);
  return adjustment.kind === 'discount' ? -size : size;
}

/**
 * The bill from its parts. `marked` is whether the order carries its payment
 * record, which is what makes a case paid: covered and not marked is still
 * part paid, and a case that owes nothing is paid when it is marked so.
 */
export function billTotals(
  subtotal: number,
  adjustments: readonly BillAdjustment[],
  payments: readonly number[],
  marked: boolean,
): BillTotals {
  const lines = cents(subtotal);
  const adjusted = cents(adjustments.reduce((sum, each) => sum + adjustmentAmount(each, lines), 0));
  const due = Math.max(0, cents(lines + adjusted));
  const paid = cents(payments.reduce((sum, each) => sum + each, 0));
  const balance = Math.max(0, cents(due - paid));
  const status: PaymentStatus = marked ? 'paid' : paid > 0 ? 'part' : 'unpaid';

  return { subtotal: lines, adjustments: adjusted, due, paid, balance, status };
}

/** A refusal, with the field of the form it belongs to. */
export interface FieldRefusal<Field extends string> {
  field: Field;
  message: string;
}

/** An adjustment that may not be kept, and why, or null. */
export function adjustmentRefusal(input: {
  kind: string | null | undefined;
  mode: string | null | undefined;
  value: number | null | undefined;
  reason: string | null | undefined;
}): FieldRefusal<'kind' | 'mode' | 'value' | 'reason'> | null {
  if (!(ADJUSTMENT_KINDS as readonly string[]).includes(input.kind ?? '')) {
    return { field: 'kind', message: BILL_REFUSAL.badKind };
  }
  if (!(ADJUSTMENT_MODES as readonly string[]).includes(input.mode ?? '')) {
    return { field: 'mode', message: BILL_REFUSAL.badMode };
  }
  if (input.value == null || !Number.isFinite(input.value) || input.value <= 0) {
    return { field: 'value', message: BILL_REFUSAL.badValue };
  }
  if (input.mode === 'percent' && input.value > 100) {
    return { field: 'value', message: BILL_REFUSAL.badPercent };
  }
  const reason = (input.reason ?? '').trim();
  if (reason === '') return { field: 'reason', message: BILL_REFUSAL.reasonRequired };
  return reason.length > BILL_REASON_MAX ? { field: 'reason', message: BILL_REFUSAL.reasonTooLong } : null;
}

/**
 * An amount that may not be taken on a case, and why, or null. `credit` is the
 * doctor's balance when the payment is to come from it, and null for money
 * received. What the case owes is said first: it is the nearer limit.
 */
export function paymentAmountRefusal(
  amount: number | null | undefined,
  balance: number,
  credit: number | null,
): FieldRefusal<'amount'> | null {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) {
    return { field: 'amount', message: BILL_REFUSAL.badAmount };
  }
  if (balance <= 0) return { field: 'amount', message: BILL_REFUSAL.nothingOwed };
  if (amount > balance) return { field: 'amount', message: moreThanOwed(balance) };
  return credit !== null && amount > credit ? { field: 'amount', message: moreThanCredit(credit) } : null;
}

/**
 * The details of a payment that may not be kept, and why, or null: how it
 * came, its reference, its note and its day. Both days are the lab's,
 * `YYYY-MM-DD`, which compare as text; `paidOn` null is now.
 */
export function paymentDetailsRefusal(
  input: {
    method: string | null | undefined;
    reference: string | null | undefined;
    note: string | null | undefined;
    paidOn: string | null | undefined;
  },
  today: string,
): FieldRefusal<'method' | 'reference' | 'note' | 'paidOn'> | null {
  if (input.method != null && !isReceivedMethod(input.method)) {
    return { field: 'method', message: BILL_REFUSAL.badMethod };
  }
  if ((input.reference ?? '').length > PAYMENT_REFERENCE_MAX) {
    return { field: 'reference', message: BILL_REFUSAL.referenceTooLong };
  }
  if ((input.note ?? '').length > PAYMENT_NOTE_MAX) {
    return { field: 'note', message: BILL_REFUSAL.noteTooLong };
  }
  return input.paidOn != null && input.paidOn > today
    ? { field: 'paidOn', message: BILL_REFUSAL.inTheFuture }
    : null;
}

// ---------------------------------------------------------------- a doctor's credit

/** The four movements of a doctor's credit. */
export const CREDIT_KINDS = ['added', 'used', 'returned', 'removed'] as const;
export type CreditKind = (typeof CREDIT_KINDS)[number];

export const CREDIT_KIND_LABEL: Record<CreditKind, string> = {
  added: 'Credit added',
  used: 'Used on a case',
  returned: 'Returned from a case',
  removed: 'Credit taken off',
};

/** A doctor's credit: the sum of every movement of it, each signed. */
export function creditBalance(entries: readonly { amount: number }[]): number {
  return cents(entries.reduce((sum, each) => sum + each.amount, 0));
}

/** Credit that may not be added, and why, or null. */
export function creditAddRefusal(input: {
  amount: number | null | undefined;
  note: string | null | undefined;
  method: string | null | undefined;
  reference: string | null | undefined;
}): FieldRefusal<'amount' | 'note' | 'method' | 'reference'> | null {
  if (input.amount == null || !Number.isFinite(input.amount) || input.amount <= 0) {
    return { field: 'amount', message: CREDIT_REFUSAL.badAmount };
  }
  const note = (input.note ?? '').trim();
  if (note === '') return { field: 'note', message: CREDIT_REFUSAL.noteRequired };
  if (note.length > CREDIT_NOTE_MAX) return { field: 'note', message: CREDIT_REFUSAL.noteTooLong };
  if (input.method != null && !isReceivedMethod(input.method)) {
    return { field: 'method', message: CREDIT_REFUSAL.badMethod };
  }
  return (input.reference ?? '').length > PAYMENT_REFERENCE_MAX
    ? { field: 'reference', message: BILL_REFUSAL.referenceTooLong }
    : null;
}

/** Credit that may not be taken off, and why, or null: only as far as there is any. */
export function creditRemoveRefusal(
  input: { amount: number | null | undefined; note: string | null | undefined },
  balance: number,
): FieldRefusal<'amount' | 'note'> | null {
  if (input.amount == null || !Number.isFinite(input.amount) || input.amount <= 0) {
    return { field: 'amount', message: CREDIT_REFUSAL.badAmount };
  }
  if (input.amount > balance) return { field: 'amount', message: moreThanBalance(balance) };
  const note = (input.note ?? '').trim();
  if (note === '') return { field: 'note', message: CREDIT_REFUSAL.noteRequired };
  return note.length > CREDIT_NOTE_MAX ? { field: 'note', message: CREDIT_REFUSAL.noteTooLong } : null;
}

// ---------------------------------------------------------------- how long a case has been open

/** The three ages the overview reads what is owed in, by the days since the case was booked. */
export const BILL_AGES = ['0-30', '31-60', 'over60'] as const;
export type BillAge = (typeof BILL_AGES)[number];

export const BILL_AGE_LABEL: Record<BillAge, string> = {
  '0-30': '0 to 30 days',
  '31-60': '31 to 60 days',
  over60: 'Over 60 days',
};

export function billAge(days: number): BillAge {
  return days <= 30 ? '0-30' : days <= 60 ? '31-60' : 'over60';
}

/** The days from one lab day to another, `YYYY-MM-DD` both, and never less than none. */
export function billAgeDays(booked: string, today: string): number {
  const day = (text: string) => Date.UTC(Number(text.slice(0, 4)), Number(text.slice(5, 7)) - 1, Number(text.slice(8, 10)));
  return Math.max(0, Math.round((day(today) - day(booked)) / 86_400_000));
}
